import { Offer, Product } from "schema-dts"
import { routes } from "src/config/routes"
import { AggregatedReviewData, DetailProductData } from "src/data/product-details/types"
import { getProductAvailable, getProductPrice, getProductRegularPrice } from "src/data/product-list/mapping"
import { META_DATA } from "src/metadata/constants"
import { createLdJsonSchemaId } from "src/seo/linkedDataHelpers"
import { createRatingLd } from "src/seo/ratingLd"
import { brandLd } from "src/seo/storeLd"
import { ProductImageSize, toProductImageCdnUrl } from "src/utilities/productImages"

export type BaseLdProduct = {
  sku: string
  name: string
  available: boolean
  images: string[] | undefined
  rating: AggregatedReviewData | undefined
}

export type LdProduct = BaseLdProduct & {
  category: string | undefined
  features: { name: string; description: string }[] | undefined
  path: string
  price: number | undefined
  regularPrice: number | undefined | null
  freeShipping: boolean | undefined
  variations: BaseLdProduct[] | undefined
}

function mapDetailProductToLdProduct(product: DetailProductData) {
  return {
    sku: product.sku,
    name: product.name,
    images: (product.images || product.variations?.[0]?.images)?.map((src) =>
      toProductImageCdnUrl(src, ProductImageSize.Huge),
    ),
    available: getProductAvailable(product),
    category: product.categories?.[0]?.name,
    path: routes.formatProduct(product.slug),
    price: getProductPrice(product),
    regularPrice: getProductRegularPrice(product),
    freeShipping: product.freeShipping,
    rating: product.rating,
    features: product.features?.map((feature) => ({
      name: feature.title,
      description: feature.description,
    })),
    variations: product.variations?.map((variation) => ({
      sku: variation.sku,
      name: variation.name,
      available: !!variation.available,
      images: variation.images.map((src) => toProductImageCdnUrl(src, ProductImageSize.Huge)),
      rating: product.rating,
    })),
  } satisfies LdProduct
}

// https://developers.google.com/search/docs/appearance/structured-data/product
// https://support.google.com/merchants/answer/6386198?hl=en&ref_topic=6386199&sjid=15288904121581552528-EU
function createProductLd(ldProduct: LdProduct) {
  const description = ldProduct.features?.map((feature) => `${feature.name}: ${feature.description}`).join(", ")
  const price = ldProduct.regularPrice || ldProduct.price
  const salePrice = ldProduct.regularPrice ? ldProduct.price : undefined
  const url = `${META_DATA.origin}${ldProduct.path}`
  const offerables = ldProduct.variations || [ldProduct]

  const ld: Product = {
    // https://schema.org/Product
    "@type": "Product",
    "@id": createLdJsonSchemaId("Product-" + ldProduct.sku),
    sku: ldProduct.sku,
    name: ldProduct.name,
    description: description,
    category: ldProduct.category,
    brand: brandLd,
    url: url,
    itemCondition: "https://schema.org/NewCondition",
    image: ldProduct.images,
    aggregateRating: ldProduct.rating && createRatingLd("Product-" + ldProduct.sku, ldProduct.rating),
    offers: offerables.map<Offer>((offerable) => {
      return {
        // https://schema.org/Offer
        "@type": "Offer",
        "@id": createLdJsonSchemaId("Offer-" + offerable.sku),
        // Seems to be inofficial but is supported by Google:
        // https://support.google.com/merchants/answer/6324471
        color: offerable.name,
        image: offerable.images,
        availability: offerable.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        priceSpecification: {
          "@type": "PriceSpecification",
          "@id": createLdJsonSchemaId("PriceSpecification-" + offerable.sku),
          price: price,
          // Seems to be inofficial but is supported by Google:
          // https://support.google.com/merchants/answer/6324471
          sale_price: salePrice,
          priceCurrency: "EUR",
        },
        shippingDetails: {
          // https://schema.org/OfferShippingDetails
          "@type": "OfferShippingDetails",
          "@id": createLdJsonSchemaId("OfferShippingDetails-" + offerable.sku),
          shippingRate: ldProduct.freeShipping
            ? {
                "@type": "MonetaryAmount",
                "@id": createLdJsonSchemaId("MonetaryAmount-" + offerable.sku),
                value: 0,
                currency: "EUR",
              }
            : undefined,
        },
      }
    }),
  }

  return ld
}

export function createDetailProductLd(product: DetailProductData) {
  const ldProduct = mapDetailProductToLdProduct(product)

  return createProductLd(ldProduct)
}
